import {
  NUPAY_CHECKOUT_CONTEXT_KEY,
  PAYMENT_ENROLLMENT_STATUS_PARAM,
} from "../checkout/states/payments/api/checkAuthStatus";
import {
  isCommerceCheckoutPath,
  isSafeCommerceReturnPath,
} from "./nupayCommerceCheckoutPath";

/**
 * Shared with commerce-portals
 * `libs/checkout/src/lib/features/paymentFlows/nubank/commerceReturnContext.ts`.
 * Commerce writes sessionStorage; legacy b2bportal uses localStorage on the same key.
 */
export { NUPAY_CHECKOUT_CONTEXT_KEY };
export { isCommerceCheckoutPath, isSafeCommerceReturnPath };

const SAFE_SEARCH_PARAM_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const UNSAFE_SEARCH_VALUE_PATTERN = /(?:^\/\/)|(?:^\w+:)|[<>]/;

export type NupayCommerceReturnContext = {
  returnPath: string;
  returnSearch: string;
  quoteId: string;
  savedAt: number;
};

export function sanitizeCommerceReturnSearch(rawSearch: string): string {
  const input = new URLSearchParams(rawSearch);
  const sanitized = new URLSearchParams();

  for (const [key, value] of input.entries()) {
    if (!SAFE_SEARCH_PARAM_KEY_PATTERN.test(key)) {
      continue;
    }
    if (UNSAFE_SEARCH_VALUE_PATTERN.test(value)) {
      continue;
    }
    sanitized.append(key, value);
  }

  return sanitized.toString();
}

export function saveCommerceReturnContext(
  context: NupayCommerceReturnContext
): boolean {
  try {
    sessionStorage.setItem(
      NUPAY_CHECKOUT_CONTEXT_KEY,
      JSON.stringify({
        platform: "commerce",
        ...context,
      })
    );
    return true;
  } catch {
    return false;
  }
}

export function buildCommerceNupayOAuthRedirectUrl(
  context: NupayCommerceReturnContext,
  oauth: { code?: string | null; error?: string | null }
): string | null {
  if (!isSafeCommerceReturnPath(context.returnPath)) {
    return null;
  }

  const searchParams = new URLSearchParams(
    sanitizeCommerceReturnSearch(context.returnSearch)
  );
  searchParams.set("quoteId", context.quoteId);

  if (oauth.code) {
    searchParams.set(PAYMENT_ENROLLMENT_STATUS_PARAM, "success");
    searchParams.set("code", oauth.code);
  } else if (oauth.error) {
    searchParams.set(PAYMENT_ENROLLMENT_STATUS_PARAM, "failure");
    searchParams.set("error", oauth.error);
  }

  const queryString = searchParams.toString();
  return queryString
    ? `${context.returnPath}?${queryString}`
    : context.returnPath;
}
